import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import Promise from 'bluebird';
import _ from 'underscore';
import { processImageListViewModelToModel, omitDeletedParameters } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';

import { AccountSettingRepository } from './utils/account-setting-repository';

const saveAdsEndpoint = 'Customers({customer_id})/Accounts({account_id})/Campaigns({campaign_id})/AdGroups({adgroup_id})/Ads';
const formatUrl = (url, pageContext, campaignId, adGroupId) => url
  .replace('{customer_id}', _.result(pageContext.CurrentCustomer, 'Id'))
  .replace('{account_id}', _.result(pageContext.CurrentAccount, 'Id'))
  .replace('{campaign_id}', campaignId)
  .replace('{adgroup_id}', adGroupId);

export class ResponsiveAdRepository {
  constructor(options) {
    this.viewContext = options.viewContext;
    this.isVideo = options.isVideo;
    this.campaignDomain = options.campaignDomain;

    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.jsdata = DependencyResolver.get('component/js-data-campaign/index');
    this.edmCampaign = DependencyResolver.get('component/edm-campaign/index');
    this.odata = DependencyResolver.get('component/http/channel/odata');
  }

  /**
   * Gets the existing ad data.
   *
   * @param {number} id - ad id to get the data.
   * @param {string} extraExpand - extra model data
   *
   * @returns {Promise} - promise of getting ad
   */
  getAdAsync(id, extraExpand) {
    const adGroupId = this.viewContext.currentAdGroup.Id;
    const campaignId = this.viewContext.currentCampaign.Id;

    const entitySet = this.edmCampaign.currentAccount
      .Campaigns.$withKey(campaignId)
      .AdGroups.$withKey(adGroupId).Ads;
    const params = {
      entitySet,
      bypassCache: true,
      queryParameters: {
        query: {
          $expand: this.isVideo ? 'Model.ResponsiveAd/Videos' : `Model.ResponsiveAd/Images${extraExpand}`,
        },
      },
    };

    return this.jsdata.Ad.find(id, params).then((ad) => {
      const adWithIds = ad;

      adWithIds.Campaign = { Id: campaignId };
      adWithIds.AdGroup = { Id: adGroupId };

      return adWithIds;
    });
  }

  /**
   * Deletes the specified ad.
   *
   * @param {number} id - id of the ad to remove
   *
   * @returns {undefined} - promise of removing ad
   */
  removeAdAsync(id) {
    const adGroupId = this.viewContext.currentAdGroup.Id;

    return this.jsdata.Ad
      .destroy(id, { adapter: 'bulk', parentId: adGroupId });
  }

  saveAdAsync(params) {
    let savePromise;

    // when use the bulkupsert API, the custom parameter need escape semicolon from ';' to '\;',
    // because the MT side will use ';' to split the several custom parameters:w
    const escapeParams = _.isArray(params.UrlCustomParameters) ?
      _.defaults({
        UrlCustomParameters: _.map(params.UrlCustomParameters, item => _.defaults({
          Value: _.contains(item.Value, ';') ? item.Value.replace(/;/g, '\\;') : item.Value,
        }, item)),
      }, params) : params;

    if (params.Id > 0) {
      savePromise = this.jsdata.ResponsiveAd.update(params.Id, escapeParams, { adapter: 'bulk', cacheResponse: false });
    } else {
      savePromise = this.jsdata.ResponsiveAd.create(escapeParams, { adapter: 'bulk', cacheResponse: false });
    }

    return savePromise;
  }

  savePlaceholderMMA(pageContext) {
    const campaignId = Number(this.viewContext.currentCampaign.Id);
    const adGroupId = Number(this.viewContext.currentAdGroup.Id);

    const requestBody = {
      '@odata.type': 'Model.ResponsiveAd',
      Campaign: {
        Id: campaignId,
      },
      AdGroup: {
        Id: adGroupId,
      },
      Type: 'Responsive',
      DisplayUrl: this.campaignDomain,
    };

    const url = formatUrl(saveAdsEndpoint, pageContext, campaignId, adGroupId);
    const option = {
      path: 'Advertiser/V2/',
      data: requestBody,
    };

    return new Promise((resolve, reject) => {
      this.odata.post(url, option)
        .then((data) => {
          resolve(data);
        })
        .catch((response) => {
          reject(_.result(response, ['responseJSON', 'value'], null));
        });
    });
  }

  saveMMABulkConversion(params) {
    const ids = _.map(params, ad => ad.Id);
    const savePromise = this.jsdata.ResponsiveAd.updateMany(ids, params, { adapter: 'bulk', cacheResponse: false });

    return savePromise;
  }

  processImageAndSaveSearchAdAsync(data, imageOData, showImageOptedIn, isMultimediaAd = true, isMultimediaAdinMSANEnabled = false, isMMAV2Enabled = false, UiUnsupportedRatios = []) {
    return processImageListViewModelToModel(data.Images.ImageList, imageOData, isMultimediaAd, isMultimediaAdinMSANEnabled, isMMAV2Enabled, UiUnsupportedRatios).then((result) => {
      const {
        mtModel,
        imageToGenerateThumbnail,
      } = result;

      const params = _.defaults(mtModel, data);

      return Promise.all([
        this.saveAdAsync(params),
        showImageOptedIn ? AccountSettingRepository.updateAccountSettings({ AllowCrawlImagesFromLandingPage: 'true' }) : Promise.resolve(),
      ])
        .then(([responseData]) => {
          if (_.isFinite(Number(responseData.Id))) {
            _.defaults(responseData, {
              Type: 'Responsive',
              ThumbnailForAdList: imageToGenerateThumbnail.ThumbnailForAdList,
              ImageList: data.Images.ImageList,
            });
            return omitDeletedParameters(responseData);
          }

          return false;
        });
    });
  }

  processImageAndSaveToClientAsync(data, imageOData, showImageOptedIn, isMultimediaAd = true, isMultimediaAdinMSANEnabled = false, isMMAV2Enabled = false, UiUnsupportedRatios = []) {
    return Promise.all([
      processImageListViewModelToModel(data.Images.ImageList, imageOData, isMultimediaAd, isMultimediaAdinMSANEnabled, isMMAV2Enabled, UiUnsupportedRatios),
      showImageOptedIn ? AccountSettingRepository.updateAccountSettings({ AllowCrawlImagesFromLandingPage: 'true' }) : Promise.resolve(),
    ])
      .then(([result]) => {
        const {
          mtModel,
          imageToGenerateThumbnail,
        } = result;

        const params = _.defaults(mtModel, data, {
          Type: 'Responsive',
          ThumbnailForAdList: imageToGenerateThumbnail.ThumbnailForAdList,
          ImageList: data.Images.ImageList,
        });

        return omitDeletedParameters(params);
      });
  }
}
